import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const SETTINGS_DOC = doc(db, 'settings', 'academy');
const AdminSettingsPage = () => {
  const [form, setForm] = useState({
    name: '', address: '', phone: '', hours: '',
    locationLat: '', locationLng: '', locationRadius: 100,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(SETTINGS_DOC);
        if (snap.exists()) {
          const d = snap.data();
          setForm({
            name: d.name || '', address: d.address || '',
            phone: d.phone || '', hours: d.hours || '',
            locationLat: d.location?.lat ?? '', locationLng: d.location?.lng ?? '',
            locationRadius: d.locationRadius ?? 100,
          });
        }
      } catch (err) {
        console.error('설정 로드 실패:', err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        name: form.name, address: form.address,
        phone: form.phone, hours: form.hours,
        locationRadius: Number(form.locationRadius) || 100,
      };
      if (form.locationLat && form.locationLng) {
        data.location = { lat: Number(form.locationLat), lng: Number(form.locationLng) };
      }
      await setDoc(SETTINGS_DOC, data, { merge: true });
      alert('저장되었습니다.');
    } catch (err) {
      console.error('설정 저장 실패:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  // 다음 주소 검색
  const handleSearchAddress = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setForm(f => ({ ...f, address: data.roadAddress || data.jibunAddress }));
      },
    }).open();
  };

  // 학원 주소로 위경도 가져오기 (Nominatim geocoding)
  const handleGetLocationFromAddress = async () => {
    if (!form.address) { alert('주소를 먼저 입력해주세요.'); return; }
    setGettingLocation(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.address)}&countrycodes=kr&limit=1`);
      const data = await res.json();
      if (data.length > 0) {
        setForm(f => ({
          ...f,
          locationLat: Number(data[0].lat).toFixed(6),
          locationLng: Number(data[0].lon).toFixed(6),
        }));
      } else {
        alert('주소로 좌표를 찾을 수 없습니다. 주소를 확인해주세요.');
      }
    } catch {
      alert('좌표 변환에 실패했습니다.');
    } finally {
      setGettingLocation(false);
    }
  };

  if (!loaded) return null;

  return (
    <Container>
      <PageTitle>설정</PageTitle>

      <Section>
        <SectionTitle>학원 정보</SectionTitle>
        <Card>
            <FormRow>
              <Label>학원명</Label>
              <Input value={form.name} onChange={set('name')} />
            </FormRow>
            <FormRow>
              <Label>주소</Label>
              <AddressRow>
                <Input value={form.address} onChange={set('address')} readOnly style={{ cursor: 'pointer', flex: 1 }} onClick={handleSearchAddress} />
                <AddressBtn onClick={handleSearchAddress}>주소 검색</AddressBtn>
              </AddressRow>
              <HelpText>이 주소는 클래스 출석 모드를 '위치 기반(학생)'으로 설정할 때 학생 출석 체크의 기본 위치 정보로 사용됩니다.</HelpText>
            </FormRow>
            <FormRow>
              <Label>전화번호</Label>
              <Input value={form.phone} onChange={set('phone')} />
            </FormRow>
            <FormRow>
              <Label>운영 시간</Label>
              <Input value={form.hours} onChange={set('hours')} />
            </FormRow>

            <LocationSection>
              <LocationHeader>
                <Label style={{ marginBottom: 0 }}>학원 위치 좌표</Label>
                <LocationBtn onClick={handleGetLocationFromAddress} disabled={gettingLocation || !form.address}>
                  {gettingLocation ? '변환 중...' : '학원주소로 위경도 가져오기'}
                </LocationBtn>
              </LocationHeader>
              <LocationHelpText>학원에서 이 버튼을 눌러 좌표를 등록하세요. 학생 셀프 출석 시 이 좌표 기준으로 거리를 확인합니다.</LocationHelpText>
              <LocationGrid>
                <LocationField>
                  <SmallLabel>위도</SmallLabel>
                  <Input type="number" step="any" placeholder="37.XXXXXX" value={form.locationLat} onChange={set('locationLat')} />
                </LocationField>
                <LocationField>
                  <SmallLabel>경도</SmallLabel>
                  <Input type="number" step="any" placeholder="127.XXXXXX" value={form.locationLng} onChange={set('locationLng')} />
                </LocationField>
                <LocationField>
                  <SmallLabel>허용 반경 (m)</SmallLabel>
                  <Input type="number" value={form.locationRadius} onChange={set('locationRadius')} />
                </LocationField>
              </LocationGrid>
              {form.locationLat && form.locationLng && (
                <LocationStatus>
                  위도 {form.locationLat}, 경도 {form.locationLng} (반경 {form.locationRadius}m)
                </LocationStatus>
              )}
            </LocationSection>

            <SaveButton onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </SaveButton>
        </Card>
      </Section>
    </Container>
  );
};

export default AdminSettingsPage;

const Container = styled.div``;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
`;

const Section = styled.div`max-width: 640px;`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #EAEDF3;
  padding: 20px;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  box-sizing: border-box;
  &:focus { border-color: #4A7CFE; box-shadow: 0 0 0 3px #EEF2FF; outline: none; }
`;

const AddressRow = styled.div`display:flex;gap:8px;`;
const AddressBtn = styled.button`
  padding:10px 16px;border-radius:8px;background:#4A7CFE;color:white;
  font-size:13px;font-weight:600;white-space:nowrap;flex-shrink:0;
  &:hover{background:#3B6DE8;}
`;

const HelpText = styled.p`
  font-size: 12px;
  color: #3B6DE8;
  background: #EEF2FF;
  border-left: 3px solid #4A7CFE;
  padding: 8px 12px;
  margin-top: 8px;
  border-radius: 0 6px 6px 0;
  line-height: 1.5;
`;

const LocationSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
`;

const LocationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const LocationBtn = styled.button`
  padding: 6px 14px;
  border-radius: 6px;
  background: #4A7CFE;
  color: white;
  font-size: 12px;
  font-weight: 600;
  &:hover { background: #3B6DE8; }
  &:disabled { opacity: 0.6; }
`;

const LocationHelpText = styled.p`
  font-size: 12px;
  color: #6B7280;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const LocationField = styled.div``;

const SmallLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  margin-bottom: 4px;
`;

const LocationStatus = styled.p`
  margin-top: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #22C55E;
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  background: #4A7CFE;
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  &:hover { background: #3B6DE8; }
  &:disabled { opacity: 0.5; }
`;

